// ABOUTME: React Query mutation hooks for creating, updating, and deleting truck settings configurations.
// ABOUTME: Transforms form values to API request shape and invalidates queries on success.

import { useMutation, useQueryClient } from '@tanstack/react-query'

import { api } from '../../../../App.store'
import { TruckSettingsApiResponse, TruckSettingsFormValues, UpsertTrucksRequest } from '../types/truckSettings'
import { toCreateScopeAndConfigRequest } from '../utils/pendingScope'
import type { PendingScopeRequest } from '../context/EntityFormContext'

export const toApiRequest = (values: TruckSettingsFormValues): UpsertTrucksRequest => {
  const trucks = values.availableTrucks?.trucks ?? []
  const truckSizeUomId = values.availableTrucks?.truckSizeUomId ?? ''
  const defaultCode = (values.availableTrucks?.defaultTruckCode ?? '').trim()
  const defaultTruck = trucks.find((t) => t.code === defaultCode)
  const derivedDefaultTruckSize = defaultTruck?.truckSize ?? values.defaultTruckSize

  return {
    defaultTruckSize: {
      isOverridden: true,
      value: derivedDefaultTruckSize ?? null,
    },
    availableTrucks: {
      isOverridden: true,
      value: {
        truckSizeUomId,
        defaultTruckCode: defaultCode,
        trucks: trucks.map((t) => ({
          code: t.code.trim(),
          description: t.description,
          truckSize: t.truckSize ?? 0,
          orderable: t.orderable,
        })),
      },
    },
  }
}

export const useCreateTruckSettings = (countryId: string | undefined, businessLine: string | undefined) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ scopeId, values }: { scopeId: string; values: TruckSettingsFormValues }) => {
      const res = await api.post<TruckSettingsApiResponse>(
        `/tuc-configs/trucks/${scopeId}`,
        toApiRequest(values)
      )
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['trucksConfig', countryId, businessLine?.toUpperCase()] })
      queryClient.invalidateQueries({ queryKey: ['navigatorScopes', 'trucks', countryId, businessLine] })
    },
  })
}

export const useCreateTruckSettingsWithScope = (countryId: string | undefined, businessLine: string | undefined) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ scope, values }: { scope: PendingScopeRequest; values: TruckSettingsFormValues }) => {
      const res = await api.post<TruckSettingsApiResponse>(
        '/tuc-configs/trucks',
        toCreateScopeAndConfigRequest(scope, toApiRequest(values))
      )
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['trucksConfig', countryId, businessLine?.toUpperCase()] })
      queryClient.invalidateQueries({ queryKey: ['navigatorScopes', 'trucks', countryId, businessLine] })
    },
  })
}

export const useUpdateTruckSettings = (countryId: string | undefined, businessLine: string | undefined) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ scopeId, values }: { scopeId: string; values: TruckSettingsFormValues }) => {
      const res = await api.put<TruckSettingsApiResponse>(
        `/tuc-configs/trucks/${scopeId}`,
        toApiRequest(values)
      )
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['trucksConfig', countryId, businessLine?.toUpperCase()] })
      queryClient.invalidateQueries({ queryKey: ['navigatorScopes', 'trucks', countryId, businessLine] })
    },
  })
}

export const useDeleteTruckSettings = (countryId: string | undefined, businessLine: string | undefined) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (scopeId: string) => {
      await api.delete(`/tuc-configs/trucks/${scopeId}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['trucksConfig', countryId, businessLine?.toUpperCase()] })
      queryClient.invalidateQueries({ queryKey: ['navigatorScopes', 'trucks', countryId, businessLine] })
    },
  })
}
